import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { businessApi, paymentApi } from '../services/api';
import { toast } from 'sonner';
import axios from 'axios';
import { Star, Check, Building2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FeatureMyBusiness = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isBusinessOwner, loading: authLoading } = useAuth();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    axios.get(`${API}/pricing`).then(r => setPricing(r.data)).catch(() => setPricing(null));
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !isBusinessOwner) {
      navigate('/business-login');
      return;
    }
    loadBusiness();
    
    // Check for cancelled payment
    if (searchParams.get('cancelled') === 'true') {
      toast.info('Payment was cancelled');
    }
  }, [isAuthenticated, isBusinessOwner, authLoading, navigate, searchParams]);

  const loadBusiness = async () => {
    try {
      const response = await businessApi.getMine();
      if (response.data) {
        setBusiness(response.data);
      } else {
        toast.error('You need a business listing first');
        navigate('/submit-business');
      }
    } catch (error) {
      console.error('Error loading business:', error);
      navigate('/submit-business');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (productType) => {
    if (!business) return;
    
    if (business.status !== 'approved') {
      toast.error('Your business must be approved before purchasing featured placement');
      return;
    }
    
    setProcessingPayment(true);
    try {
      const response = await paymentApi.createBusinessCheckout(productType, business.id);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-12" data-testid="feature-business-page">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <div className="text-center mb-12">
          <div className="w-20 h-20 rounded-2xl bg-accent/20 flex items-center justify-center mx-auto mb-6">
            <Star className="w-10 h-10 text-accent" />
          </div>
          <h1 className="text-4xl font-bold text-neutral-900 mb-4" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Feature Your Business
          </h1>
          <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
            Get premium visibility on Explore MHC and reach more customers in Martinsville and Henry County
          </p>
        </div>

        {/* Current Business */}
        {business && (
          <div className="bg-white rounded-2xl p-6 border border-neutral-200 mb-12 max-w-xl mx-auto">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-secondary to-secondary-light flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                {business.business_name.charAt(0)}
              </div>
              <div>
                <h3 className="font-bold text-lg">{business.business_name}</h3>
                <p className="text-neutral-600 text-sm">{business.primary_category}</p>
                <span className={`text-xs font-semibold ${business.status === 'approved' ? 'text-green-600' : 'text-amber-600'}`}>
                  {business.status === 'approved' ? 'Approved' : 'Pending Approval'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          {/* Weekly Plan */}
          <div className="pricing-card">
            <h3 className="text-xl font-bold text-neutral-900 mb-2">Weekly Featured</h3>
            <p className="text-neutral-600 mb-6">Perfect for a quick visibility boost</p>
            
            <div className="pricing-amount mb-6">
              ${pricing?.business_weekly?.amount ?? 10}
              {pricing?.business_weekly?.promo_active && pricing?.business_weekly?.base_amount !== pricing?.business_weekly?.amount && (
                <span className="text-lg text-neutral-400 line-through ml-2">${pricing?.business_weekly?.base_amount}</span>
              )}
              <span> / week</span>
            </div>

            <ul className="space-y-3 text-left mb-8">
              <li className="flex items-center gap-3">
                <Check className="w-5 h-5 text-secondary" />
                <span>Homepage Featured Spotlight</span>
              </li>
              <li className="flex items-center gap-3">
                <Check className="w-5 h-5 text-secondary" />
                <span>Featured badge on listing</span>
              </li>
              <li className="flex items-center gap-3">
                <Check className="w-5 h-5 text-secondary" />
                <span>Priority in search results</span>
              </li>
              <li className="flex items-center gap-3">
                <Check className="w-5 h-5 text-secondary" />
                <span>7 days of visibility</span>
              </li>
            </ul>

            <button
              onClick={() => handlePurchase('business_weekly')}
              disabled={processingPayment || business?.status !== 'approved'}
              className="btn-secondary w-full"
              data-testid="purchase-weekly-btn"
            >
              {processingPayment ? 'Processing...' : 'Get Started'}
            </button>
          </div>

          {/* Monthly Plan */}
          <div className="pricing-card popular">
            <h3 className="text-xl font-bold text-neutral-900 mb-2">Monthly Featured</h3>
            <p className="text-neutral-600 mb-6">Best value for sustained growth</p>
            
            <div className="pricing-amount mb-6">
              ${pricing?.business_monthly?.amount ?? 30}
              {pricing?.business_monthly?.promo_active && pricing?.business_monthly?.base_amount !== pricing?.business_monthly?.amount && (
                <span className="text-lg text-neutral-400 line-through ml-2">${pricing?.business_monthly?.base_amount}</span>
              )}
              <span> / month</span>
            </div>

            <ul className="space-y-3 text-left mb-8">
              <li className="flex items-center gap-3">
                <Check className="w-5 h-5 text-secondary" />
                <span>Homepage Featured Spotlight</span>
              </li>
              <li className="flex items-center gap-3">
                <Check className="w-5 h-5 text-secondary" />
                <span>Featured badge on listing</span>
              </li>
              <li className="flex items-center gap-3">
                <Check className="w-5 h-5 text-secondary" />
                <span>Priority in search results</span>
              </li>
              <li className="flex items-center gap-3">
                <Check className="w-5 h-5 text-secondary" />
                <span>30 days of visibility</span>
              </li>
              <li className="flex items-center gap-3">
                <Check className="w-5 h-5 text-accent" />
                <span className="font-semibold text-accent">Save 35% vs weekly</span>
              </li>
            </ul>

            <button
              onClick={() => handlePurchase('business_monthly')}
              disabled={processingPayment || business?.status !== 'approved'}
              className="btn-accent w-full"
              data-testid="purchase-monthly-btn"
            >
              {processingPayment ? 'Processing...' : 'Get Featured'}
            </button>
          </div>
        </div>

        {business?.status !== 'approved' && (
          <div className="text-center mt-8 p-4 bg-amber-50 rounded-xl border border-amber-200 max-w-xl mx-auto">
            <p className="text-amber-800">
              Your business must be approved before you can purchase featured placement.
              Please wait for admin approval.
            </p>
          </div>
        )}

        {business?.is_featured && (
          <div className="text-center mt-8 p-4 bg-green-50 rounded-xl border border-green-200 max-w-xl mx-auto">
            <p className="text-green-800">
              Your business is currently featured until {new Date(business.featured_until).toLocaleDateString()}.
              You can extend your featured status by purchasing again.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeatureMyBusiness;
